import { useEffect, useState, useMemo } from "react";
import { useLenis } from "../hooks/useLenis";
import { api } from "../lib/api";
import ProfileHeader from "../components/site/ProfileHeader";
import SocialLinks from "../components/site/SocialLinks";
import EditorialMarquee from "../components/site/EditorialMarquee";
import BigTile from "../components/site/BigTile";
import SectionContent from "../components/site/SectionContent";
import SiteFooter from "../components/site/SiteFooter";

export default function Home() {
  useLenis();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Aly's page owns its own <title> and description now that the domain root
    // is Linkit's marketing homepage. This keeps SEO and browser-tab context
    // correct when a visitor lands directly on /aly.
    const prevTitle = document.title;
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute("content");
    document.title = "Aly Hajiani | ThatCreditCardGuy — Best Credit Cards & Rewards";
    document.querySelector('meta[name="description"]')?.setAttribute(
      "content",
      "Aly Hajiani (ThatCreditCardGuy) — the best credit cards, reward-points strategies and products I personally recommend."
    );
    api
      .get("/content")
      .then((res) => setData(res.data))
      .catch(() => setError(true));
    return () => {
      if (prevTitle) document.title = prevTitle;
      if (prevDesc) document.querySelector('meta[name="description"]')?.setAttribute("content", prevDesc);
    };
  }, []);

  const itemsBySection = useMemo(() => {
    const map = {};
    (data?.items || []).forEach((it) => {
      const key = it.section_id || "__ungrouped__";
      if (!map[key]) map[key] = [];
      map[key].push(it);
    });
    return map;
  }, [data]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6 text-center text-[var(--ink-soft)]">
        Unable to load content. Please refresh.
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--hairline)] border-t-[var(--accent)]" />
      </div>
    );
  }

  const { settings } = data;

  const tiles = Array.isArray(settings.tiles) && settings.tiles.length
    ? settings.tiles
    : [
        { id: "credit_cards", title: "Best Credit Cards 2026", subtitle: "Find the right card based on how you spend", visible: true },
        { id: "products", title: "Discounted Products (Use code ALY)", subtitle: "Products I personally use, trust & recommend", visible: true },
        { id: "consultations", title: "Consultations & Concierge", subtitle: "Personalised help & premium services", visible: true },
      ];

  const themeStyle = {
    "--ink": settings.text_primary || "#FFFFFF",
    "--ink-soft": settings.text_secondary || "rgba(242,242,242,0.68)",
    "--accent": settings.accent_color || "#C9B79C",
    background:
      settings.background_css ||
      "radial-gradient(circle at 50% 20%, #5a5148 0%, #393631 35%, #1d1d1b 100%)",
    backgroundAttachment: "fixed",
    color: settings.text_primary || "#FFFFFF",
    fontFamily: `'${settings.font_family || "Inter"}', system-ui, sans-serif`,
  };

  return (
    <div className="relative z-[2] min-h-screen" style={themeStyle}>
      <div className="mx-auto w-full max-w-[680px] px-5 pb-16 sm:px-6">
        <ProfileHeader settings={settings} />
        <SocialLinks settings={settings} />
        {settings.marquee_enabled && (
          <div className="mt-8">
            <EditorialMarquee text={settings.marquee_text} />
          </div>
        )}
        <div className="mt-8 space-y-4">
          {tiles.filter((t) => t.visible !== false).map((t, i) => {
            const sectionItems = itemsBySection[t.id] || [];
            return (
              <BigTile
                key={t.id}
                testid={`tile-${t.id}`}
                index={String(i + 1).padStart(2, "0")}
                title={t.title}
                subtitle={t.subtitle}
                count={sectionItems.length}
              >
                <SectionContent items={sectionItems} sectionId={t.id} />
              </BigTile>
            );
          })}
        </div>
        <SiteFooter settings={settings} />
      </div>
    </div>
  );
}
